import React, { useState, useCallback } from "react";
import { ChevronDown } from "react-feather";
import { EvidenceCard } from "../evidence-card/evidence-card";
import type { EvidenceCardConfig } from "../criterion-question/criterion-question";
import "./criterion-checklist.scss";

export type { EvidenceCardConfig };

export interface ChecklistOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface CriterionChecklistProps {
  stepNumber?: number | string;
  title: string;
  badge?: React.ReactNode;

  checklistName: string;
  checklistOptions: ChecklistOption[];
  checkedValues?: string[];
  onCheckChange?: (
    value: string,
    checked: boolean,
    allChecked: string[]
  ) => void;
  checklistDisabled?: boolean;
  checkedColor?: string;

  evidenceCards?: EvidenceCardConfig[];
  /** @deprecated Use evidenceCards instead */
  evidenceTitle?: string;
  /** @deprecated Use evidenceCards instead */
  evidenceStatements?: string[];
  /** @deprecated Use evidenceCards instead */
  evidenceCardBackGroundColor?: string;

  collapsible?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;

  /** Optional content rendered between the header row and the checklist options. */
  headerSlot?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  id?: string;
}

const CriterionChecklist = ({
  stepNumber,
  title,
  badge,
  checklistName,
  checklistOptions,
  checkedValues = [],
  onCheckChange,
  checklistDisabled = false,
  checkedColor,
  evidenceCards,
  evidenceTitle,
  evidenceStatements,
  evidenceCardBackGroundColor,
  collapsible = false,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  headerSlot,
  children,
  className,
  id,
}: CriterionChecklistProps) => {
  const isControlled = expandedProp !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = isControlled ? expandedProp : internalExpanded;

  const toggleExpanded = useCallback(() => {
    const next = !isExpanded;
    if (!isControlled) setInternalExpanded(next);
    onExpandedChange?.(next);
  }, [isExpanded, isControlled, onExpandedChange]);

  const titleId = id ? `${id}-title` : undefined;

  const resolvedEvidenceCards: EvidenceCardConfig[] =
    evidenceCards != null && evidenceCards.length > 0
      ? evidenceCards
      : evidenceTitle != null ||
          (evidenceStatements != null && evidenceStatements.length > 0)
        ? [
            {
              title: evidenceTitle,
              statements: evidenceStatements,
              cardBackGroundColor: evidenceCardBackGroundColor,
            },
          ]
        : [];

  const hasEvidence = resolvedEvidenceCards.length > 0;

  const handleChange = (optionValue: string, checked: boolean) => {
    const next = checked
      ? [...checkedValues, optionValue]
      : checkedValues.filter((v) => v !== optionValue);
    onCheckChange?.(optionValue, checked, next);
  };

  const colorStyle = checkedColor
    ? ({ "--checklist-checked-color": checkedColor } as React.CSSProperties)
    : undefined;

  return (
    <section
      id={id}
      className={["criterion-checklist", className]
        .filter(Boolean)
        .join(" ")}
      aria-labelledby={titleId}
      style={colorStyle}
    >
      {stepNumber != null && (
        <div className="criterion-checklist__step-column">
          <span className="criterion-checklist__step">{stepNumber}.</span>
        </div>
      )}

      <div className="criterion-checklist__body">
        <div className="criterion-checklist__header">
          <h3 id={titleId} className="criterion-checklist__title">
            {title}
          </h3>
          <div className="criterion-checklist__header-actions">
            {badge}
            {collapsible && (
              <button
                type="button"
                className={[
                  "criterion-checklist__toggle",
                  isExpanded ? "" : "criterion-checklist__toggle--collapsed",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={toggleExpanded}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                <ChevronDown size={16} />
              </button>
            )}
          </div>
        </div>

        <div
          className={[
            "criterion-checklist__collapsible",
            isExpanded
              ? "criterion-checklist__collapsible--expanded"
              : "criterion-checklist__collapsible--collapsed",
          ].join(" ")}
          aria-hidden={!isExpanded}
        >
          <div className="criterion-checklist__collapsible-inner">
            {headerSlot != null && (
              <div className="criterion-checklist__header-slot">
                {headerSlot}
              </div>
            )}
            <div className="criterion-checklist__content">
              <div
                className="criterion-checklist__options"
                role="group"
                aria-label={title}
              >
                {checklistOptions.map((option) => {
                  const isDisabled = checklistDisabled || option.disabled;
                  const isChecked = checkedValues.includes(option.value);
                  const optionId = `${checklistName}-${option.value}`;

                  return (
                    <label
                      key={option.value}
                      className={[
                        "criterion-checklist__option",
                        isDisabled
                          ? "criterion-checklist__option--disabled"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      htmlFor={optionId}
                    >
                      <input
                        className="criterion-checklist__input"
                        type="checkbox"
                        id={optionId}
                        name={checklistName}
                        value={option.value}
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={(e) =>
                          handleChange(option.value, e.target.checked)
                        }
                      />
                      <span className="criterion-checklist__box">
                        <svg
                          className="criterion-checklist__check-icon"
                          viewBox="0 0 448 512"
                          fill="currentColor"
                        >
                          <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" />
                        </svg>
                      </span>
                      <span className="criterion-checklist__label">
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>

              {hasEvidence &&
                resolvedEvidenceCards.map((card, index) => (
                  <EvidenceCard
                    key={index}
                    title={card.title}
                    statements={card.statements}
                    cardBackGroundColor={card.cardBackGroundColor}
                    titleAction={card.titleAction}
                  />
                ))}
            </div>

            {children != null && (
              <div className="criterion-checklist__children">{children}</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { CriterionChecklist };
export default CriterionChecklist;
